import React, { useMemo, useState } from 'react';
import Image, { ImageProps } from 'next/image';
import { ImageDomains } from '@/constant/image-domains';

type ImageWithErrorHandlingProps = {
  errorImage?: string;
  skipDomainCheck?: boolean;
} & ImageProps;

const hostAllowed = (host: string) =>
  ImageDomains.some((d) => host === d || host.endsWith(`.${d}`));

function getSafeSrc(src: any, errorImage: string, skipDomainCheck: boolean) {
  // 정적 import나 내부 경로(static) → 통과
  if (typeof src !== 'string') return src;
  if (!src) return errorImage; // 빈 문자열 방지

  if (src.startsWith('http:') || src.startsWith('https:')) {
    try {
      const url = new URL(src);

      if (!skipDomainCheck && !hostAllowed(url.hostname)) {
        return errorImage;
      }

      return src; // 정상 URL + 허용된 호스트
    } catch {
      return errorImage; // 파싱 실패 → 잘못된 URL → 에러이미지
    }
  }
  return src;
}

const ImageWithErrorHandling = ({
  src,
  errorImage = '/images/dummy/dummy.png',
  skipDomainCheck = false,
  ...props
}: ImageWithErrorHandlingProps) => {
  const [errored, setErrored] = useState(false);
  const safeSrc = useMemo(
    () => getSafeSrc(src, errorImage, skipDomainCheck),
    [src, errorImage, skipDomainCheck],
  );

  const finalSrc = errored ? errorImage : safeSrc;

  return (
    <Image
      {...props}
      src={finalSrc as any}
      onError={(e) => {
        setErrored(true);
        props.onError?.(e as any);
      }}
      onLoadingComplete={(img) => {
        setErrored(false);
        props.onLoadingComplete?.(img as any);
      }}
    />
  );
};

export default React.memo(
  ImageWithErrorHandling,
  (a, b) =>
    a.src === b.src &&
    a.errorImage === b.errorImage &&
    a.skipDomainCheck === b.skipDomainCheck &&
    a.width === b.width &&
    a.height === b.height &&
    a.className === b.className,
);
